import { defineResource, deriveResource } from 'pulse/resources';
import { defaultPropertyGroupsResolver } from './resolver';
import { propertiesPersistedPromise } from 'framework-data-schema-resolvers/properties/makePropertiesClient';
export const RootPropertyGroups = defineResource({
  typeName: 'hs-entities:RootPropertyGroups',
  resolver: defaultPropertyGroupsResolver,
  identifyInstance: ({
    objectTypeId
  }) => objectTypeId
}).onChange(({
  state,
  params,
  dataChanged,
  hadDataBeforeChange
}) => {
  if (state.data && dataChanged && hadDataBeforeChange) {
    propertiesPersistedPromise.syncIntoCache(Promise.resolve(state.data), {
      frameworkTypeIdentifier: params.objectTypeId,
      query: {
        showHighlySensitiveProperties: true
      },
      // HACK: This isn't used for the cache key, just an unfortunate part of the api
      httpClient: {}
    });
  }
}).register();
export const propertiesIdentity = ({
  objectTypeId,
  showHighlySensitiveProperties = false
}) => `${objectTypeId}-showHSP:${showHighlySensitiveProperties}`;

// If showHSP is false, we need to mark any property with a dataSensitivity of "high" as hidden: true.
// We always fetch with showHighlySensitiveProperties: true because we can do this derivation.
// This logic originally lived in FDSR: https://github.com/HubSpotEngineering/framework-data-schema-resolvers/blob/master/framework-data-schema-resolvers/static/js/properties/makePropertiesClient.ts#L177-L224
// It **must** mirror the logic in the BE: https://git.hubteam.com/HubSpotProtected/InboundDbProperties/blob/3a9dbb099b1ed16a2bdf16b9b010cf8b0db42207/InboundDbPropertiesService/src/main/java/com/hubspot/inbounddb/properties/service/helpers/PropertiesResponseTransform.java#L56-L72
const PropertyGroupsHSPTransform = deriveResource('hs-entities:PropertyGroupsHSPTransform').from(RootPropertyGroups).mapParams(params => ({
  objectTypeId: params.objectTypeId
})).transformToTarget(({
  sourceData,
  targetParams
}) => {
  const originalHiddenValues = {};
  const groups = [];

  // Return as-is if the caller wants to see HSPs
  if (targetParams.showHighlySensitiveProperties) {
    return {
      groups: sourceData
    };
  }
  for (const group of sourceData) {
    if (!group.propertyDefinitions) {
      groups.push(group);
      continue;
    }
    const propertyDefinitions = group.propertyDefinitions.slice();
    for (let i = 0; i < propertyDefinitions.length; i++) {
      const def = propertyDefinitions[i];
      if (def.property.dataSensitivity === 'high') {
        originalHiddenValues[def.property.name] = def.property.hidden;
        propertyDefinitions[i] = Object.assign({}, def, {
          property: Object.assign({}, def.property, {
            hidden: true
          })
        });
      }
    }
    groups.push(Object.assign({}, group, {
      propertyDefinitions
    }));
  }
  return {
    originalHiddenValues,
    groups
  };
}).transformToSource(({
  targetData,
  targetParams
}) => {
  if (targetParams.showHighlySensitiveProperties) {
    return targetData.groups;
  }

  // Restore original hidden values
  return targetData.groups.map(group => {
    var _group$propertyDefini;
    return Object.assign({}, group, {
      propertyDefinitions: (_group$propertyDefini = group.propertyDefinitions) === null || _group$propertyDefini === void 0 ? void 0 : _group$propertyDefini.map(definition => {
        var _targetData$originalH, _targetData$originalH2;
        return Object.assign({}, definition, {
          property: Object.assign({}, definition.property, {
            hidden: (_targetData$originalH = (_targetData$originalH2 = targetData.originalHiddenValues) === null || _targetData$originalH2 === void 0 ? void 0 : _targetData$originalH2[definition.property.name]) !== null && _targetData$originalH !== void 0 ? _targetData$originalH : definition.property.hidden
          })
        });
      })
    });
  });
}).withIdentity(propertiesIdentity).register();
export const PropertyGroups = deriveResource('hs-entities:PropertyGroups').from(PropertyGroupsHSPTransform).transformToTarget(({
  sourceData
}) => sourceData.groups).transformToSource(({
  sourceData,
  targetData
}) => sourceData && Object.assign({}, sourceData, {
  groups: targetData
})).withIdentity(propertiesIdentity).register();